import { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Grid,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import StructuredModal from 'src/views/components/structuredModal/structuredModal';
import ControlledTextField from 'src/views/components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from 'src/views/components/controlledComponents/ControlledSelectField.component';
import ControlledDateField from 'src/views/components/controlledComponents/ControlledDateField.component';
import ControlledFileUploadField from 'src/views/components/controlledComponents/ControlledFileUploadField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  useHealthAndSafetyState,
  useHealthAndSafetyActions,
  EViewState,
} from '../../../../contexts/healthAndSafety.context';
import { useHealthAndSafety } from '../../../../hooks/useHealthAndSafety';
import { THealthCheckup } from 'src/apis/administration/administration.interfaces';
import { TKeyValue } from 'src/utils/commonInterfaces';
import dayjs, { Dayjs } from 'dayjs';
import {
  isPickerDateRangeOrdered,
  zOptionalNullablePickerDate,
  zRequiredPickerDate,
} from 'src/utils/zodFormHelpers';

type HealthCheckupFormData = {
  personId: string;
  personType: 'STUDENT' | 'STAFF';
  checkupDate: Dayjs | null;
  checkupType:
  | 'ANNUAL'
  | 'MEDICAL'
  | 'DENTAL'
  | 'VISION'
  | 'HEARING'
  | 'VACCINATION';
  doctorName: string;
  hospitalClinic: string;
  findings: string;
  recommendations: string;
  nextCheckupDate?: Dayjs | null;
  isCleared: boolean;
  notes?: string;
  attachments?: any[];
};

const getHealthCheckupFormSchema = (translateText: (key: string) => string) =>
  z
    .object({
    personId: z.string().min(1, translateText('common.fieldRequired')),
    personType: z.enum(['STUDENT', 'STAFF'], {
      required_error: translateText('common.fieldRequired'),
    }),
    checkupDate: zRequiredPickerDate({
      requiredMessage: translateText('common.fieldRequired'),
      invalidMessage: translateText('common.dateInvalid'),
    }),
    checkupType: z.enum(
      ['ANNUAL', 'MEDICAL', 'DENTAL', 'VISION', 'HEARING', 'VACCINATION'],
      {
        required_error: translateText('common.fieldRequired'),
      }
    ),
    doctorName: z.string().min(1, translateText('common.fieldRequired')),
    hospitalClinic: z.string().min(1, translateText('common.fieldRequired')),
    findings: z.string().min(1, translateText('common.fieldRequired')),
    recommendations: z.string().min(1, translateText('common.fieldRequired')),
    nextCheckupDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    isCleared: z.boolean(),
    notes: z.string().optional(),
    attachments: z.array(z.any()).optional(),
  })
    .refine(
      (data) =>
        !data.nextCheckupDate ||
        isPickerDateRangeOrdered(data.checkupDate, data.nextCheckupDate),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['nextCheckupDate'],
      }
    );

const AddEditHealthCheckupView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedCheckup, editMode, viewState, currentViewType } =
    useHealthAndSafetyState();
  const { setViewState, setEditMode, selectCheckup } =
    useHealthAndSafetyActions();
  const { createCheckup, updateCheckup, isLoading, errorMessage } =
    useHealthAndSafety();

  const isEditMode = editMode === 'edit' && selectedCheckup;
  const isOpen =
    (viewState === EViewState.ADD || viewState === EViewState.EDIT) &&
    currentViewType === 'checkups';

  const checkupFormSchema = getHealthCheckupFormSchema(translateText);

  const defaultValues: HealthCheckupFormData = {
    personId: '',
    personType: 'STUDENT',
    checkupDate: dayjs(),
    checkupType: 'ANNUAL',
    doctorName: '',
    hospitalClinic: '',
    findings: '',
    recommendations: '',
    nextCheckupDate: null,
    isCleared: false,
    notes: '',
    attachments: [],
  };

  const methods = useForm<HealthCheckupFormData>({
    resolver: zodResolver(checkupFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (isEditMode && selectedCheckup) {
      reset({
        personId: selectedCheckup.personId,
        personType: selectedCheckup.personType,
        checkupDate: selectedCheckup.checkupDate
          ? dayjs(selectedCheckup.checkupDate)
          : dayjs(),
        checkupType: selectedCheckup.checkupType,
        doctorName: selectedCheckup.doctorName,
        hospitalClinic: selectedCheckup.hospitalClinic,
        findings: selectedCheckup.findings,
        recommendations: selectedCheckup.recommendations,
        nextCheckupDate: selectedCheckup.nextCheckupDate
          ? dayjs(selectedCheckup.nextCheckupDate)
          : null,
        isCleared: selectedCheckup.isCleared,
        notes: selectedCheckup.notes || '',
        attachments: selectedCheckup.attachments || [],
      });
    } else if (isOpen && !isEditMode) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, selectedCheckup, isOpen, reset]);

  const handleClose = () => {
    if (isEditMode) {
      // Return to details view when canceling from edit mode
      setViewState(EViewState.DETAILS);
    } else {
      // Return to list view when canceling from add mode
      setViewState(EViewState.LIST);
      selectCheckup(undefined);
    }
    setEditMode(undefined);
    reset(defaultValues);
  };

  const onSubmitForm = async (data: HealthCheckupFormData) => {
    try {
      const checkupData: Omit<
        THealthCheckup,
        'id' | 'createdAt' | 'updatedAt'
      > = {
        personId: data.personId,
        personType: data.personType,
        checkupDate: data.checkupDate ? data.checkupDate.toDate() : new Date(),
        checkupType: data.checkupType,
        doctorName: data.doctorName,
        hospitalClinic: data.hospitalClinic,
        findings: data.findings,
        recommendations: data.recommendations,
        nextCheckupDate: data.nextCheckupDate
          ? data.nextCheckupDate.toDate()
          : undefined,
        isCleared: data.isCleared,
        notes: data.notes,
        attachments: data.attachments,
      };

      if (isEditMode && selectedCheckup) {
        await updateCheckup(selectedCheckup.id, checkupData);
      } else {
        await createCheckup(checkupData);
      }

      handleClose();
    } catch (error) {
      console.error('Error saving health checkup:', error);
    }
  };

  const personTypeOptions: TKeyValue<string>[] = [
    {
      key: 'STUDENT',
      value:
        translateText('administration.healthAndSafety.values.student') ||
        'Student',
    },
    {
      key: 'STAFF',
      value:
        translateText('administration.healthAndSafety.values.staff') || 'Staff',
    },
  ];

  const checkupTypeOptions: TKeyValue<string>[] = [
    {
      key: 'ANNUAL',
      value:
        translateText('administration.healthAndSafety.values.annual') ||
        'Annual',
    },
    {
      key: 'MEDICAL',
      value:
        translateText('administration.healthAndSafety.values.medical') ||
        'Medical',
    },
    {
      key: 'DENTAL',
      value:
        translateText('administration.healthAndSafety.values.dental') ||
        'Dental',
    },
    {
      key: 'VISION',
      value:
        translateText('administration.healthAndSafety.values.vision') ||
        'Vision',
    },
    {
      key: 'HEARING',
      value:
        translateText('administration.healthAndSafety.values.hearing') ||
        'Hearing',
    },
    {
      key: 'VACCINATION',
      value:
        translateText('administration.healthAndSafety.values.vaccination') ||
        'Vaccination',
    },
  ];

  return (
    <div data-testid="add-edit-health-checkup-container">
      <StructuredModal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          isEditMode
            ? translateText(
              'administration.healthAndSafety.editHealthCheckup'
            ) || 'Edit Health Checkup'
            : translateText(
              'administration.healthAndSafety.addHealthCheckup'
            ) || 'Add Health Checkup'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isEditMode
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={isLoading}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          errorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="health-checkup-error-message"
            >
              {errorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="health-checkup-form"
          >
            <Grid container spacing={3} padding={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {translateText(
                    'administration.healthAndSafety.labels.basicInformation'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="personId"
                  label={translateText(
                    'administration.healthAndSafety.columns.personId'
                  )}
                  name="personId"
                  control={control}
                  errorMessage={errors.personId?.message}
                  required
                  data-testid="person-id-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  id="personType"
                  label={translateText(
                    'administration.healthAndSafety.columns.personType'
                  )}
                  name="personType"
                  control={control}
                  itemList={personTypeOptions}
                  errorMessage={errors.personType?.message}
                  isRequired
                  data-testid="person-type-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledDateField
                  id="checkupDate"
                  label={translateText(
                    'administration.healthAndSafety.columns.checkupDate'
                  )}
                  name="checkupDate"
                  control={control}
                  errorMessage={errors.checkupDate?.message}
                  required
                  data-testid="checkup-date-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  id="checkupType"
                  label={translateText(
                    'administration.healthAndSafety.columns.checkupType'
                  )}
                  name="checkupType"
                  control={control}
                  itemList={checkupTypeOptions}
                  errorMessage={errors.checkupType?.message}
                  isRequired
                  data-testid="checkup-type-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="doctorName"
                  label={translateText(
                    'administration.healthAndSafety.columns.doctor'
                  )}
                  name="doctorName"
                  control={control}
                  errorMessage={errors.doctorName?.message}
                  required
                  data-testid="doctor-name-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="hospitalClinic"
                  label={translateText(
                    'administration.healthAndSafety.labels.hospitalClinic'
                  )}
                  name="hospitalClinic"
                  control={control}
                  errorMessage={errors.hospitalClinic?.message}
                  required
                  data-testid="hospital-clinic-field"
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  id="findings"
                  label={translateText(
                    'administration.healthAndSafety.labels.findings'
                  )}
                  name="findings"
                  control={control}
                  errorMessage={errors.findings?.message}
                  multiline
                  rows={3}
                  required
                  data-testid="findings-field"
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  id="recommendations"
                  label={translateText(
                    'administration.healthAndSafety.labels.recommendations'
                  )}
                  name="recommendations"
                  control={control}
                  errorMessage={errors.recommendations?.message}
                  multiline
                  rows={3}
                  required
                  data-testid="recommendations-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledDateField
                  id="nextCheckupDate"
                  label={translateText(
                    'administration.healthAndSafety.columns.nextCheckup'
                  )}
                  name="nextCheckupDate"
                  control={control}
                  errorMessage={errors.nextCheckupDate?.message}
                  data-testid="next-checkup-date-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={methods.watch('isCleared')}
                      onChange={(e) =>
                        methods.setValue('isCleared', e.target.checked)
                      }
                      data-testid="is-cleared-switch"
                    />
                  }
                  label={translateText(
                    'administration.healthAndSafety.status.cleared'
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  id="notes"
                  label={translateText('common.remarks')}
                  name="notes"
                  control={control}
                  errorMessage={errors.notes?.message}
                  multiline
                  rows={2}
                  data-testid="notes-field"
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledFileUploadField
                  id="attachments"
                  label={translateText(
                    'administration.healthAndSafety.labels.attachments'
                  )}
                  name="attachments"
                  control={control}
                  multiple
                  data-testid="attachments-field"
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditHealthCheckupView;
